import add from 'date-fns/add';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import differenceInCalendarMonths from 'date-fns/differenceInCalendarMonths';
import format from 'date-fns/format';

const diffFns = {
  day: differenceInCalendarDays,
  month: differenceInCalendarMonths,
};

const durations = {
  day: 'days',
  month: 'months',
};

const getByKeyOrDefault = (key) => (obj, defaultVal) => obj[key] || defaultVal;

const buildRange = (from, to, calcDiff, calcPointDate) => {
  const points: IPoint[] = [];
  const diff = calcDiff(to, from!);
  const createPoint = (xValue) => ({ x: xValue, y: 0 });

  if (!Number.isInteger(diff)) {
    return points;
  }

  if (diff < 0) {
    return points;
  }

  for (let i = 0; i < diff; i++) {
    const date = calcPointDate(i);
    const point = createPoint(date);
    points.push(point);
  }

  return points;
};

export interface IPoint {
  x: string;
  y: number;
}

export const calcComplementedPoints = ({
  points = [],
  groupBy,
  from,
  to,
  today = new Date(),
}: {
  points: IPoint[];
  groupBy: string;
  from?: Date | null;
  to?: Date | null;
  today?: Date;
}) => {
  if (points.length === 0) {
    return points;
  }

  const formatDate = (date) => format(date, 'yyyy-M-d');
  const getByGroupOrDefault = getByKeyOrDefault(groupBy);
  const calcDiff = getByGroupOrDefault(diffFns, diffFns.month);
  const duration = getByGroupOrDefault(durations, durations.month);

  // @ts-ignore
  points.sort((a, b) => new Date(a.x) - new Date(b.x));

  const firstPoint: IPoint = points[0];
  const lastPoint: IPoint = points[points.length - 1];

  let pointsBefore: IPoint[] = [];
  let pointsAfter: IPoint[] = [];

  if (firstPoint?.x && from) {
    const parsedDateOfFirstPoint = new Date(firstPoint.x);
    const calcPointDate = (i) => formatDate(add(from!, { [duration]: i }));

    pointsBefore = buildRange(from!, parsedDateOfFirstPoint, calcDiff, calcPointDate);
  }

  const dateTo = to || today;
  if (lastPoint?.x) {
    const parsedDateOfLastPoint = new Date(lastPoint.x);

    const restrict = (date) => (differenceInCalendarDays(date, today) > 0 ? today : date);
    const calcPointDate = (i) =>
      formatDate(restrict(add(parsedDateOfLastPoint, { [duration]: i + 1 })));

    pointsAfter = buildRange(parsedDateOfLastPoint, dateTo!, calcDiff, calcPointDate);
  }

  return [pointsBefore, pointsAfter];
};
